import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import { 
  Shield, 
  User, 
  Key, 
  Phone, 
  ArrowLeft, 
  CheckCircle,
  Loader2,
  Lock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const AdminLoginPage = () => {
  const [step, setStep] = useState(1); // 1: credentials, 2: verification
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [userCode, setUserCode] = useState("");
  const [password, setPassword] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [loading, setLoading] = useState(false);
  const { setToken, setUser } = useAuth();
  const navigate = useNavigate();

  const handleInitiateLogin = async (e) => {
    e.preventDefault();
    
    if (!employeeNumber || !userCode || !password || !mobileNumber) {
      toast.error("Please fill in all fields");
      return;
    }

    if (password.length !== 4 || !/^\d+$/.test(password)) {
      toast.error("Password must be exactly 4 digits");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/auth/initiate`, {
        employee_number: employeeNumber,
        user_code: userCode,
        password: password,
        mobile_number: mobileNumber
      });
      
      setDemoCode(response.data.demo_code);
      toast.success(`Verification code sent to ${response.data.mobile_masked}`, {
        description: `Demo Code: ${response.data.demo_code}`,
        duration: 10000
      });
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (verificationCode.length !== 6) {
      toast.error("Please enter the 6-digit verification code");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/auth/verify`, {
        employee_number: employeeNumber,
        verification_code: verificationCode
      });
      
      const { token, user } = response.data;
      localStorage.setItem("cdace_token", token);
      localStorage.setItem("cdace_admin_user", JSON.stringify(user));
      
      toast.success("Admin authentication successful");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1c2d] flex" data-testid="admin-login-page">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: "url('https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=2000&auto=format&fit=crop')",
            filter: "brightness(0.25)"
          }}
        />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-sm bg-[#c9a227] flex items-center justify-center">
                <Shield className="w-8 h-8 text-[#0b1c2d]" />
              </div>
              <div>
                <h1 className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Administrator Portal
                </h1>
                <p className="text-slate-400">CDACE–ClemTech HR & Project Monitoring</p>
              </div>
            </div>
          </div>
          <div className="space-y-4 text-slate-400">
            <p className="flex items-center gap-3">
              <span className="w-2 h-2 bg-[#c9a227] rounded-full"></span>
              HR & Workforce Management
            </p>
            <p className="flex items-center gap-3">
              <span className="w-2 h-2 bg-[#c9a227] rounded-full"></span>
              Timesheet & Leave Approvals
            </p>
            <p className="flex items-center gap-3">
              <span className="w-2 h-2 bg-[#c9a227] rounded-full"></span>
              Project Resource Allocation
            </p>
            <p className="flex items-center gap-3">
              <span className="w-2 h-2 bg-[#c9a227] rounded-full"></span>
              Payroll & Financial Integration
            </p>
          </div>
          <div className="mt-12 p-4 bg-slate-900/50 rounded-sm border border-[#c9a227]/20">
            <p className="text-sm text-[#c9a227] font-semibold mb-2">Demo Credentials:</p>
            <div className="grid grid-cols-2 gap-2 text-xs text-slate-400">
              <p>Employee: <span className="text-white">EMP001</span></p>
              <p>Usercode: <span className="text-white">ADM001</span></p>
              <p>Password: <span className="text-white">1234</span></p>
              <p>Mobile: <span className="text-white">+27123456789</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <button 
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Portal Selection</span>
          </button>

          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-sm bg-[#c9a227] flex items-center justify-center">
                <Shield className="w-6 h-6 text-[#0b1c2d]" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Administrator Portal
            </h1>
            <p className="text-slate-400 mt-1">HR & Project Monitoring</p>
          </div>

          <div className="glass-card p-8">
            {step === 1 ? (
              <>
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Secure Login
                  </h2>
                  <p className="text-slate-400 mt-2">Multi-factor authentication required</p>
                </div>

                <form onSubmit={handleInitiateLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="employeeNumber" className="text-slate-300">Employee Number</Label>
                    <div className="relative mt-1">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        id="employeeNumber"
                        data-testid="admin-employee-number"
                        type="text"
                        placeholder="e.g., EMP001"
                        value={employeeNumber}
                        onChange={(e) => setEmployeeNumber(e.target.value.toUpperCase())}
                        className="pl-10 bg-slate-950/50 border-[#c9a227]/20 focus:border-[#c9a227] text-white uppercase"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="userCode" className="text-slate-300">User Code</Label>
                    <div className="relative mt-1">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        id="userCode"
                        data-testid="admin-user-code"
                        type="text"
                        placeholder="e.g., ADM001"
                        value={userCode}
                        onChange={(e) => setUserCode(e.target.value.toUpperCase())}
                        className="pl-10 bg-slate-950/50 border-[#c9a227]/20 focus:border-[#c9a227] text-white uppercase"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="password" className="text-slate-300">4-Digit PIN</Label>
                    <div className="relative mt-1">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        id="password"
                        data-testid="admin-password"
                        type="password"
                        maxLength={4}
                        placeholder="****"
                        value={password}
                        onChange={(e) => setPassword(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        className="pl-10 bg-slate-950/50 border-[#c9a227]/20 focus:border-[#c9a227] text-white tracking-widest"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="mobile" className="text-slate-300">Mobile Number</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <Input
                        id="mobile"
                        data-testid="admin-mobile"
                        type="tel"
                        placeholder="+27 xxx xxx xxxx"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value)}
                        className="pl-10 bg-slate-950/50 border-[#c9a227]/20 focus:border-[#c9a227] text-white"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Verification code will be sent to this number</p>
                  </div>

                  <Button
                    type="submit"
                    data-testid="admin-submit-btn"
                    disabled={loading}
                    className="w-full btn-primary h-12 text-base mt-6"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "Send Verification Code"
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#c9a227]/20 flex items-center justify-center">
                    <Phone className="w-8 h-8 text-[#c9a227]" />
                  </div>
                  <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Verify Your Identity
                  </h2>
                  <p className="text-slate-400 mt-2">
                    Enter the 6-digit code sent to your mobile
                  </p>
                </div>

                {/* Demo Code Display */}
                {demoCode && (
                  <div className="mb-6 p-3 bg-[#c9a227]/10 border border-[#c9a227]/30 rounded-sm text-center">
                    <p className="text-xs text-slate-400 mb-1">Demo Verification Code:</p>
                    <p className="text-2xl font-mono text-[#c9a227] tracking-widest">{demoCode}</p>
                  </div>
                )}

                <div className="mb-6">
                  <Label className="text-slate-300 text-center block mb-2">Enter 6-Digit Code</Label>
                  <Input
                    type="text"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    data-testid="admin-otp-input"
                    className="text-center text-2xl tracking-[0.5em] font-mono bg-slate-950/50 border-[#c9a227]/20 focus:border-[#c9a227] text-white h-14"
                  />
                </div>

                <Button
                  onClick={handleVerify}
                  data-testid="admin-verify-btn"
                  disabled={loading || verificationCode.length !== 6}
                  className="w-full btn-primary h-12 text-base"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Verify & Access Dashboard
                    </>
                  )}
                </Button>

                <button
                  onClick={() => {
                    setStep(1);
                    setVerificationCode("");
                    setDemoCode("");
                  }}
                  className="w-full mt-4 text-slate-400 hover:text-white text-sm"
                >
                  ← Back to credentials
                </button>
              </>
            )}
          </div>

          <div className="mt-6 text-center text-xs text-slate-500">
            <p>© 2026 CDACE–ClemTech. Authorized Personnel Only.</p>
            <p className="mt-1">Enterprise Workforce Governance System</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
